import "../i18n";
import React, { useState, useEffect } from 'react';
import Cookies from 'js-cookie';
import { Sentry } from "../sentryInstrumentReact";

import ProcessingSpinner from '../components/ProcessingSpinner';
import { defaultProductUi, updateProductUi } from './storeLogic/storeStateManager';
import VariantSelector from './VariantSelector';

function AddToCart({productId}) {
  const [productInfo, setProductInfo] = useState({});
  const [productUi, setProductUi] = useState(defaultProductUi);
  const [loading, setLoading] = useState(true);
  const [addToCartLoading, setAddToCartLoading] = useState(false);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const csrftoken = Cookies.get('csrftoken');
  
  let displayLowQtyMessage = null;
  if (selectedVariant && parseInt(selectedVariant.quantity) < 10 && parseInt(selectedVariant.quantity) > 0) {
    displayLowQtyMessage = true;
  }
  
  const maxiumQty = selectedVariant?.maximum_quantity || 9999;
  const backorderQty = productUi.quantity + (selectedVariant?.quantity_in_cart || 0) - (selectedVariant?.quantity || 0);

  useEffect(() => {
    async function fetchProductInfo() {
      try {
        const response = await fetch(`/store/_api/products/${productId}/`, {
          method: 'GET',
          headers: {"Content-Type": "application/json"},
        });

        if (!response.ok) {
          throw new Error(`Network response was not ok, status: ${response.status}, type: ${response.type}`);
        }
        const data = await response.json();
        setProductInfo(data);
        
        // Find the initial variant using the initial_variant_sku field
        if (data.variants && data.variants.length > 0) {
          const initialVariant = data.variants.find(v => v.sku === data.initial_variant_sku);
          setSelectedVariant(initialVariant);
          
          const minQuantityAtLoad = initialVariant.quantity_in_cart >= initialVariant.minimum_quantity ? 1 : initialVariant.minimum_quantity;
          setProductUi(prevProductUi => updateProductUi(initialVariant, minQuantityAtLoad, prevProductUi));
        }
        
      } catch (error) {
        if (Sentry && Sentry.getClient()) {
          Sentry.captureException(error, {stage: "error retrieving data from API"});
        }
      }
      setLoading(false);
    }
    fetchProductInfo();
  }, []);

  // Update product info when variant changes
  useEffect(() => {
    if (selectedVariant) {
      // Update the template's stock status
    const stockStatusElement = document.querySelector('.stock');
    if (stockStatusElement) {
      if (selectedVariant.quantity >= 1 || selectedVariant.downloadable) {
        stockStatusElement.textContent = window.gettext('In Stock');
        stockStatusElement.className = 'stock in-stock smallcaps';
      } else if (selectedVariant.backordering) {
        stockStatusElement.textContent = window.gettext('Available on Backorder');
        stockStatusElement.className = 'stock on-backorder smallcaps';
      } else {
        stockStatusElement.textContent = window.gettext('Out of Stock');
        stockStatusElement.className = 'stock out-of-stock smallcaps';
      }
    }

      // Update the template's h1 title element to include the variant attributes
      const titleElement = document.querySelector('.product-info h1');
      if (titleElement) {
        const hasAttributes = selectedVariant.attributes && Object.values(selectedVariant.attributes).some(Boolean);
        titleElement.textContent = hasAttributes ? 
          `${productInfo.title} - ${Object.values(selectedVariant.attributes).join(' - ')}` :
          productInfo.title;
      }

      // Update SKU in the template (keep it in same location)
      const skuElement = document.querySelector('.product-meta .sku');
      if (skuElement) {
        skuElement.innerHTML = `<strong>SKU:</strong> ${selectedVariant.sku}`;
      }

      // Update productUi with new variant data
      setProductUi(prevProductUi => updateProductUi(selectedVariant, 1, prevProductUi));

      // Update product info with selected variant data
      setProductInfo(prevInfo => ({
        ...prevInfo,
        quantity: selectedVariant.quantity,
        price: selectedVariant.price,
        current_price: selectedVariant.current_price,
        is_on_sale: selectedVariant.is_on_sale,
        is_discounted_from_retail: selectedVariant.is_discounted_from_retail,
        retail_price: selectedVariant.retail_price,
        discount_percent: selectedVariant.discount_percent,
        minimum_quantity: selectedVariant.minimum_quantity,
        maximum_quantity: selectedVariant.maximum_quantity,
        backordering: selectedVariant.backordering,
        downloadable: selectedVariant.downloadable,
        manage_stock: selectedVariant.manage_stock,
      }));
    }
  }, [selectedVariant]);

  function onChangeHandler(event) {
    setProductUi(updateProductUi(selectedVariant, event.target.value, productUi));
  }

  function onClickHandler(changeAmt) {
    setProductUi(updateProductUi(selectedVariant, parseInt(productUi.quantity) + changeAmt, productUi));
  }

  async function addToCartOnClickHandler() {
    setAddToCartLoading(true);
    try {
      const response = await fetch("/store/_api/cart/", {
        method: "POST",
        headers: {"Content-Type": "application/json", "X-CSRFToken": csrftoken},
        body: JSON.stringify({variant: selectedVariant.id, quantity: productUi.quantity}),
      })

      const data = await response.json();
      if (!response.ok) {
        throw data;
      }

      window.location.href = window.gettext("/store/cart/");

    } catch (error) {
      const values = Object.values(error);
      setAddToCartLoading(false);
      setProductUi(prevProductUi => {
        return {
          ...prevProductUi,
          errors: values,
        }
      });
    }
  }

  if(loading) {
    return (
      <ProcessingSpinner />
    );
  }

  if (!selectedVariant) {
    return (
      <div>Loading product information...</div>
    );
  }


  return (
    <>
      <VariantSelector 
        variants={productInfo.variants} 
        selectedVariant={selectedVariant} 
        onVariantChange={setSelectedVariant} 
      />
      {productUi.isDiscountedFromRetail ? (
        <p className="price mt-1 mb-1">
          <strong className="sale-price">${productUi.cleanSalePrice}</strong> 
          <strike className="normal-price">${productUi.cleanFullPrice}</strike> 
          <span className="savings">{window.gettext("Save")} {productUi.percentSavings}%</span>
        </p> 
      ) : (
        <p className="price mt-1"><strong className="normal-price no-sale">${productUi.cleanSalePrice}</strong></p>
      )}
          
      {(selectedVariant.quantity_break_ranges?.length || 0) > 0 &&
        <table className="table table-bordered table--quantity-discounts mb-1">
          <thead>
            <tr>
              <th>{window.gettext("Quantity")}</th>
              <th>1–9</th>
              {selectedVariant.quantity_break_ranges.map(range => 
                <th key={range}>{range}</th>
              )}
            </tr>
          </thead>
          <tbody>
            <tr>
              <th>{window.gettext("Price per item")}</th>
              <td>${parseFloat(selectedVariant?.price || 0).toFixed(2)}</td>
              {selectedVariant.quantity_break_prices.map(price => 
                <td key={price}>${price.toFixed(2)}</td>
              )}
            </tr>
          </tbody>
        </table>
      }

      {displayLowQtyMessage &&
        <p className="low-stock">
          {window.gettext("Only ") + selectedVariant.quantity + window.gettext(" left in stock–order soon.")}
        </p>
      }
      {productUi.canAddMore === false ? (
        <p className="alert alert-warning">{window.gettext("Max quantity: ") + (selectedVariant?.maximum_quantity || 0) + window.gettext(". You cannot add any more of this resource to your cart.")}</p>
      ) : (
        <div className="cart-actions" method="post">
          <div className="cart-counter">
            <button className="minus cart-btn" id="btn-quantity-down" disabled={productUi.minusDisabled} onClick={() => onClickHandler(-1)}>
              <svg viewBox="0 0 24 24" fill="currentColor" height="16px" width="16px" className="icon icon--counter">
                <path d="M17 11a1 1 0 010 2H7a1 1 0 010-2h10z">
                </path>
              </svg>
            </button>
            <input 
              type="number" 
              min={selectedVariant.minimum_quantity} 
              max={maxiumQty} 
              id="quantity" 
              pattern="[0-9]*" 
              className="qty text" 
              aria-label="Product quantity" 
              size="4" 
              step="1" 
              placeholder="" 
              autoComplete="off" 
              value={productUi.quantity} 
              onChange={onChangeHandler}
              readOnly={selectedVariant.maximum_quantity === 1}
            />
            <button className="plus cart-btn" id="btn-quantity-up" disabled={productUi.plusDisabled} onClick={() => onClickHandler(1)}>
              <svg fill="currentColor" viewBox="0 0 16 16" height="16px" width="16px" className="icon icon--counter">
                <path d="M8 4a.5.5 0 01.5.5v3h3a.5.5 0 010 1h-3v3a.5.5 0 01-1 0v-3h-3a.5.5 0 010-1h3v-3A.5.5 0 018 4z">
                </path>
              </svg>
            </button>
          </div>
          {addToCartLoading === true ? (
            <ProcessingSpinner />
          ) : (
            <a 
              id="add-to-cart-btn" 
              className={`btn btn-primary btn-spotlight ${selectedVariant?.stock_status === 'out_of_stock' ? 'disabled' : ''}`} 
              onClick={selectedVariant?.stock_status === 'out_of_stock' ? undefined : addToCartOnClickHandler}
              style={{ opacity: selectedVariant?.stock_status === 'out_of_stock' ? 0.5 : 1, cursor: selectedVariant?.stock_status === 'out_of_stock' ? 'not-allowed' : 'pointer' }}
            >
              {selectedVariant?.stock_status === 'out_of_stock' ? window.gettext("Out of Stock") : window.gettext("Add to Cart")}
            </a>
          )}
        </div>
      )}

      {productUi.backorder && 
        <div>
          {selectedVariant?.quantity === 0 ? 
            <>
              <p className="alert alert-warning">
                {window.gettext("This item is on backorder. You can pay now and we will ship the entire order as soon as all resources are available in our warehouse.")} 
              </p>
            </>
          : 
            <>
              <p className="alert alert-warning">
                {window.gettext("Quantity available now: ") + selectedVariant?.quantity}<br />
                {window.gettext("Quantity to put on backorder: ") + backorderQty}<br />
                {window.gettext("You can pay now and we will ship the entire order as soon as all resources are available in our warehouse.")}  
              </p>
            </>
          }
        </div>
      }
      
      {!addToCartLoading && productUi.errors.length > 0 && 
        <div className="messages-wrapper" style={{maxWidth: "var(--narrow)", marginInline: "auto"}}>
          <ul className="messages list-unstyled">
            {productUi.errors.map((error, index) => (
              <li key={index} className="alert alert-warning">{error}</li>
            ))}
          </ul>
        </div>
      }
    </>
  )
}

export default AddToCart;
